/**
 * AI Intelligence Page - Phase 8
 * Transparent, explainable investment intelligence
 * All scores and recommendations are rule-based and auditable
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Brain,
  Target,
  Warning,
  Shield,
  TrendUp,
  TrendDown,
  Lightning,
  ChartLine,
  CaretRight,
  CaretDown,
  ArrowsClockwise,
  CheckCircle,
  WarningCircle,
  Info,
  Scales,
  Clock,
  Users,
  CurrencyEur,
  House,
  Wrench,
  Flag,
  ListChecks,
} from '@phosphor-icons/react';
import { LoadingPage, EmptyState } from '../components/common';
import { PageHelpBlock } from '../components/help';
import { pageExplanations } from '../content/helpContent';
import { dealsApi, intelligenceApi } from '../utils/api';
import { formatCurrency, formatPercent } from '../utils/formatters';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { cn } from '../lib/utils';

// Score configurations
const DEAL_SCORE_CONFIG = {
  excellent: { color: 'text-emerald-600', bg: 'bg-emerald-100', label: 'Excellent' },
  good: { color: 'text-blue-600', bg: 'bg-blue-100', label: 'Bon' },
  average: { color: 'text-amber-600', bg: 'bg-amber-100', label: 'Moyen' },
  weak: { color: 'text-orange-600', bg: 'bg-orange-100', label: 'Faible' },
  poor: { color: 'text-red-600', bg: 'bg-red-100', label: 'Mauvais' },
};

const RISK_LEVEL_CONFIG = {
  low: { color: 'text-emerald-600', bg: 'bg-emerald-100', label: 'Risque Faible' },
  medium: { color: 'text-amber-600', bg: 'bg-amber-100', label: 'Risque Moyen' },
  high: { color: 'text-orange-600', bg: 'bg-orange-100', label: 'Risque Élevé' },
  critical: { color: 'text-red-600', bg: 'bg-red-100', label: 'Risque Critique' },
};

const CONFIDENCE_CONFIG = {
  high: { color: 'text-emerald-600', bg: 'bg-emerald-100', label: 'Confiance Élevée' },
  medium: { color: 'text-amber-600', bg: 'bg-amber-100', label: 'Confiance Moyenne' },
  low: { color: 'text-orange-600', bg: 'bg-orange-100', label: 'Confiance Faible' },
  very_low: { color: 'text-red-600', bg: 'bg-red-100', label: 'Confiance Très Faible' },
};

const ACTION_CONFIG = {
  strong_buy: { color: 'bg-emerald-500', textColor: 'text-white', icon: TrendUp },
  buy_below_target: { color: 'bg-blue-500', textColor: 'text-white', icon: Target },
  negotiate: { color: 'bg-amber-500', textColor: 'text-white', icon: Scales },
  monitor: { color: 'bg-slate-500', textColor: 'text-white', icon: Clock },
  high_risk: { color: 'bg-orange-500', textColor: 'text-white', icon: Warning },
  reject: { color: 'bg-red-500', textColor: 'text-white', icon: TrendDown },
};

const FLAG_SEVERITY_CONFIG = {
  critical: { color: 'bg-red-100 text-red-700 border-red-200' },
  high: { color: 'bg-orange-100 text-orange-700 border-orange-200' },
  medium: { color: 'bg-amber-100 text-amber-700 border-amber-200' },
  low: { color: 'bg-slate-100 text-slate-700 border-slate-200' },
};

// Score gauge component
const ScoreGauge = ({ score, label, size = 'lg', colorConfig }) => {
  const config = colorConfig || (
    score >= 70 ? { color: 'text-emerald-500' } :
    score >= 50 ? { color: 'text-amber-500' } : { color: 'text-red-500' }
  );
  
  const radius = size === 'lg' ? 54 : 40;
  const circumference = 2 * Math.PI * radius;
  const progress = (score / 100) * circumference;
  const viewBox = size === 'lg' ? '0 0 120 120' : '0 0 100 100';
  const center = size === 'lg' ? 60 : 50;
  const strokeWidth = size === 'lg' ? 8 : 6;
  
  return (
    <div className="relative inline-flex items-center justify-center">
      <svg className={size === 'lg' ? 'w-28 h-28' : 'w-20 h-20'} viewBox={viewBox}>
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-secondary"
        />
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference - progress}
          className={config.color}
          style={{ transform: 'rotate(-90deg)', transformOrigin: 'center' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={cn('text-2xl font-bold', config.color)}>{score.toFixed(0)}</span>
        {label && <span className="text-xs text-muted-foreground">{label}</span>}
      </div>
    </div>
  );
};

// Score breakdown component
const ScoreBreakdownCard = ({ title, components, icon: Icon }) => (
  <Card data-testid={`breakdown-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardHeader className="pb-2">
      <CardTitle className="flex items-center gap-2 text-base">
        {Icon && <Icon size={18} />}
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="space-y-4">
        {components.map((comp, idx) => (
          <div key={idx}>
            <div className="flex items-center justify-between text-sm mb-1">
              <span>{comp.name}</span>
              <span className="font-medium">{comp.score.toFixed(0)}/100</span>
            </div>
            <Progress value={comp.score} className="h-2 mb-1" />
            {comp.explanation && (
              <p className="text-xs text-muted-foreground">{comp.explanation}</p>
            )}
            {comp.factors_positive?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {comp.factors_positive.map((f, i) => (
                  <Badge key={i} variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                    {f}
                  </Badge>
                ))}
              </div>
            )}
            {comp.factors_negative?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {comp.factors_negative.map((f, i) => (
                  <Badge key={i} variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                    {f}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

// Main component
export const AIIntelligence = () => {
  const [searchParams] = useSearchParams();
  const initialDealId = searchParams.get('deal');
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deals, setDeals] = useState([]);
  const [selectedDealId, setSelectedDealId] = useState(initialDealId || '');
  const [intelligence, setIntelligence] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Fetch deals for selector
  const fetchDeals = useCallback(async () => {
    try {
      const data = await dealsApi.getSummaries();
      setDeals(data);
      if (!selectedDealId && data.length > 0) {
        setSelectedDealId(data[0].id);
      }
    } catch (err) {
      console.error('Failed to fetch deals:', err);
    }
  }, [selectedDealId]);

  // Fetch intelligence for selected deal
  const fetchIntelligence = useCallback(async () => {
    if (!selectedDealId) return;
    
    try {
      setLoading(true);
      const data = await intelligenceApi.getFullAnalysis(selectedDealId);
      setIntelligence(data);
    } catch (err) {
      console.error('Failed to fetch intelligence:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedDealId]);

  useEffect(() => {
    fetchDeals();
  }, [fetchDeals]);

  useEffect(() => {
    if (selectedDealId) {
      fetchIntelligence();
    }
  }, [selectedDealId, fetchIntelligence]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchIntelligence();
    setRefreshing(false);
  };

  if (loading && !intelligence) {
    return <LoadingPage message="Analyse du deal en cours..." />;
  }

  const dealScoreConfig = DEAL_SCORE_CONFIG[intelligence?.deal_score?.score_label] || DEAL_SCORE_CONFIG.average;
  const riskConfig = RISK_LEVEL_CONFIG[intelligence?.risk_score?.risk_level] || RISK_LEVEL_CONFIG.medium;
  const confidenceConfig = CONFIDENCE_CONFIG[intelligence?.confidence_score?.confidence_level] || CONFIDENCE_CONFIG.medium;
  const actionConfig = ACTION_CONFIG[intelligence?.recommendation?.action] || ACTION_CONFIG.monitor;
  const ActionIcon = actionConfig.icon;

  return (
    <div className="space-y-6 p-6" data-testid="ai-intelligence-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Brain size={28} />
            Intelligence IA
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Analyse d'investissement transparente et explicable
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedDealId} onValueChange={setSelectedDealId}>
            <SelectTrigger className="w-64" data-testid="deal-selector">
              <SelectValue placeholder="Sélectionner un deal" />
            </SelectTrigger>
            <SelectContent>
              {deals.map((deal) => (
                <SelectItem key={deal.id} value={deal.id}>
                  {deal.deal_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="gap-2"
          >
            <ArrowsClockwise size={16} className={refreshing ? 'animate-spin' : ''} />
            Actualiser
          </Button>
        </div>
      </div>

      {intelligence ? (
        <>
          {/* Top Score Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-testid="score-cards">
            {/* Deal Score */}
            <Card className="border-2 border-[#3A4A40]">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Score Deal</p>
                    <p className={cn('text-3xl font-bold mt-1', dealScoreConfig.color)}>
                      {intelligence.deal_score.overall_score.toFixed(0)}
                    </p>
                    <Badge className={cn('mt-2', dealScoreConfig.bg, dealScoreConfig.color)}>
                      {dealScoreConfig.label}
                    </Badge>
                  </div>
                  <ScoreGauge score={intelligence.deal_score.overall_score} size="md" />
                </div>
              </CardContent>
            </Card>

            {/* Risk Score */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Score Risque</p>
                    <p className={cn('text-3xl font-bold mt-1', riskConfig.color)}>
                      {intelligence.risk_score.overall_score.toFixed(0)}
                    </p>
                    <Badge className={cn('mt-2', riskConfig.bg, riskConfig.color)}>
                      {riskConfig.label}
                    </Badge>
                  </div>
                  <ScoreGauge 
                    score={intelligence.risk_score.overall_score} 
                    size="md"
                    colorConfig={{ color: intelligence.risk_score.overall_score >= 50 ? 'text-red-500' : 'text-emerald-500' }}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Confidence Score */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Confiance</p>
                    <p className={cn('text-3xl font-bold mt-1', confidenceConfig.color)}>
                      {intelligence.confidence_score.overall_score.toFixed(0)}
                    </p>
                    <Badge className={cn('mt-2', confidenceConfig.bg, confidenceConfig.color)}>
                      {confidenceConfig.label}
                    </Badge>
                  </div>
                  <ScoreGauge score={intelligence.confidence_score.overall_score} size="md" />
                </div>
              </CardContent>
            </Card>

            {/* Recommendation */}
            <Card className={cn('border-2', actionConfig.color.replace('bg-', 'border-'))}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className={cn('p-3 rounded-full', actionConfig.color)}>
                    <ActionIcon size={24} className={actionConfig.textColor} weight="bold" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Recommandation</p>
                    <p className="text-xl font-bold mt-1">{intelligence.recommendation.action_label}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Confiance: {intelligence.recommendation.confidence}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Key Metrics Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Rendement Attendu</p>
                <p className={cn(
                  'text-2xl font-bold font-tabular',
                  intelligence.expected_roi_percent >= 15 ? 'text-emerald-600' :
                  intelligence.expected_roi_percent >= 10 ? 'text-amber-600' : 'text-red-600'
                )}>
                  {intelligence.expected_roi_percent.toFixed(1)}%
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Profit Attendu</p>
                <p className="text-2xl font-bold font-tabular">
                  {formatCurrency(intelligence.expected_profit)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Acquisition Cible</p>
                <p className="text-2xl font-bold font-tabular">
                  {formatCurrency(intelligence.target_acquisition)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Sortie Cible</p>
                <p className="text-2xl font-bold font-tabular">
                  {formatCurrency(intelligence.target_exit)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="overview">Aperçu</TabsTrigger>
              <TabsTrigger value="deal-score">Score Deal</TabsTrigger>
              <TabsTrigger value="risk-score">Score Risque</TabsTrigger>
              <TabsTrigger value="confidence">Confiance</TabsTrigger>
              <TabsTrigger value="memo">Mémo IA</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6 mt-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recommendation Panel */}
                <Card className="border-2 border-[#3A4A40] bg-[#F8FAF9]" data-testid="recommendation-panel">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Lightning size={18} className="text-[#3A4A40]" />
                      Recommandation
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-lg font-bold">{intelligence.recommendation.headline}</p>
                    <p className="text-sm text-muted-foreground mt-2">{intelligence.recommendation.rationale}</p>
                    
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      {intelligence.recommendation.top_reasons.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-emerald-700 mb-2">Points Forts</p>
                          <ul className="space-y-1">
                            {intelligence.recommendation.top_reasons.map((reason, i) => (
                              <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                                <CheckCircle size={14} className="text-emerald-600 mt-0.5 shrink-0" />
                                {reason}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {intelligence.recommendation.top_risks.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-red-700 mb-2">Risques Clés</p>
                          <ul className="space-y-1">
                            {intelligence.recommendation.top_risks.map((risk, i) => (
                              <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                                <WarningCircle size={14} className="text-red-600 mt-0.5 shrink-0" />
                                {risk}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Negotiation Guidance */}
                    {intelligence.recommendation.negotiation_guidance && (
                      <div className="mt-4 pt-4 border-t">
                        <p className="text-xs font-medium mb-2">Guide de Négociation</p>
                        <div className="grid grid-cols-3 gap-2 text-center">
                          <div className="p-2 bg-white rounded-lg border">
                            <p className="text-xs text-muted-foreground">Offre Cible</p>
                            <p className="font-bold font-tabular text-sm">
                              {formatCurrency(intelligence.recommendation.negotiation_guidance.target_bid)}
                            </p>
                          </div>
                          <div className="p-2 bg-white rounded-lg border">
                            <p className="text-xs text-muted-foreground">Offre Max</p>
                            <p className="font-bold font-tabular text-sm">
                              {formatCurrency(intelligence.recommendation.negotiation_guidance.max_bid)}
                            </p>
                          </div>
                          <div className="p-2 bg-white rounded-lg border">
                            <p className="text-xs text-muted-foreground">Limite</p>
                            <p className="font-bold font-tabular text-sm">
                              {formatCurrency(intelligence.recommendation.negotiation_guidance.walk_away_price)}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Attention Flags */}
                <Card data-testid="attention-flags">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Flag size={18} />
                      Points d'Attention ({intelligence.attention_flags.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {intelligence.attention_flags.length > 0 ? (
                      <div className="space-y-3">
                        {intelligence.attention_flags.map((flag, i) => (
                          <div key={i} className={cn('p-3 rounded-lg border', FLAG_SEVERITY_CONFIG[flag.severity]?.color)}>
                            <div className="flex items-start gap-3">
                              <Badge variant="outline" className={cn('shrink-0 capitalize', FLAG_SEVERITY_CONFIG[flag.severity]?.color)}>
                                {flag.severity}
                              </Badge>
                              <div>
                                <p className="font-medium text-sm">{flag.title}</p>
                                <p className="text-xs text-muted-foreground mt-1">{flag.description}</p>
                                <p className="text-xs font-medium mt-2">{flag.action_required}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <CheckCircle size={32} className="mx-auto mb-2 text-emerald-500" />
                        <p>Aucun point d'attention</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Priority Actions */}
              {intelligence.priority_actions.length > 0 && (
                <Card data-testid="priority-actions">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <ListChecks size={18} />
                      Actions Prioritaires
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ol className="space-y-2">
                      {intelligence.priority_actions.map((action, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <span className="w-6 h-6 rounded-full bg-[#3A4A40] text-white text-xs flex items-center justify-center shrink-0 font-medium">
                            {i + 1}
                          </span>
                          <span className="text-sm">{action}</span>
                        </li>
                      ))}
                    </ol>
                  </CardContent>
                </Card>
              )}

              {/* Scenario Sensitivity */}
              {intelligence.scenario_sensitivity && (
                <Card data-testid="scenario-sensitivity">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <ChartLine size={18} />
                      Sensibilité aux Scénarios
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="p-3 bg-red-50 rounded-lg border border-red-200 text-center">
                        <p className="text-xs text-muted-foreground">Conservateur</p>
                        <p className={cn(
                          'text-lg font-bold font-tabular',
                          intelligence.scenario_sensitivity.conservative_case_profit >= 0 ? 'text-red-700' : 'text-red-600'
                        )}>
                          {formatCurrency(intelligence.scenario_sensitivity.conservative_case_profit)}
                        </p>
                      </div>
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-center">
                        <p className="text-xs text-muted-foreground">Cas de Base</p>
                        <p className="text-lg font-bold font-tabular text-blue-700">
                          {formatCurrency(intelligence.scenario_sensitivity.base_case_profit)}
                        </p>
                      </div>
                      <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200 text-center">
                        <p className="text-xs text-muted-foreground">Agressif</p>
                        <p className="text-lg font-bold font-tabular text-emerald-700">
                          {formatCurrency(intelligence.scenario_sensitivity.aggressive_case_profit)}
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">{intelligence.scenario_sensitivity.break_even_sensitivity}</p>
                    
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div className="p-3 bg-secondary/30 rounded-lg">
                        <p className="text-xs text-muted-foreground">Principal Facteur de Hausse</p>
                        <p className="font-medium text-sm">{intelligence.scenario_sensitivity.biggest_upside_driver}</p>
                      </div>
                      <div className="p-3 bg-secondary/30 rounded-lg">
                        <p className="text-xs text-muted-foreground">Principal Risque de Baisse</p>
                        <p className="font-medium text-sm">{intelligence.scenario_sensitivity.biggest_downside_risk}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Deal Score Tab */}
            <TabsContent value="deal-score" className="space-y-6 mt-6">
              <div className="flex items-center gap-6 mb-6">
                <ScoreGauge score={intelligence.deal_score.overall_score} label={dealScoreConfig.label} size="lg" />
                <div>
                  <h2 className="text-xl font-bold">Détail du Score Deal</h2>
                  <p className="text-sm text-muted-foreground">
                    Analyse pondérée de l'attractivité de l'investissement
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ScoreBreakdownCard
                  title="Acquisition & Prix"
                  icon={CurrencyEur}
                  components={[
                    intelligence.deal_score.acquisition_score,
                    intelligence.deal_score.comp_support_score
                  ]}
                />
                <ScoreBreakdownCard
                  title="Rénovation & Délais"
                  icon={Wrench}
                  components={[
                    intelligence.deal_score.renovation_risk_score,
                    intelligence.deal_score.timeline_risk_score
                  ]}
                />
                <ScoreBreakdownCard
                  title="Sortie & Rentabilité"
                  icon={TrendUp}
                  components={[
                    intelligence.deal_score.exit_realism_score,
                    intelligence.deal_score.profitability_score
                  ]}
                />
                <ScoreBreakdownCard
                  title="Confiance Partenaire"
                  icon={Users}
                  components={[intelligence.deal_score.partner_confidence_score]}
                />
              </div>
            </TabsContent>

            {/* Risk Score Tab */}
            <TabsContent value="risk-score" className="space-y-6 mt-6">
              <div className="flex items-center gap-6 mb-6">
                <ScoreGauge 
                  score={intelligence.risk_score.overall_score} 
                  label={riskConfig.label} 
                  size="lg"
                  colorConfig={{ color: intelligence.risk_score.overall_score >= 50 ? 'text-red-500' : 'text-emerald-500' }}
                />
                <div>
                  <h2 className="text-xl font-bold">Risk Score Breakdown</h2>
                  <p className="text-sm text-muted-foreground">
                    Higher score = more risk. Target below 40.
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ScoreBreakdownCard
                  title="Pricing & Exit Risk"
                  icon={CurrencyEur}
                  components={[
                    intelligence.risk_score.pricing_risk,
                    intelligence.risk_score.exit_risk
                  ]}
                />
                <ScoreBreakdownCard
                  title="Execution Risk"
                  icon={Wrench}
                  components={[
                    intelligence.risk_score.renovation_risk,
                    intelligence.risk_score.partner_execution_risk
                  ]}
                />
                <ScoreBreakdownCard
                  title="Risque Temps & Données"
                  icon={Clock}
                  components={[
                    intelligence.risk_score.time_risk,
                    intelligence.risk_score.data_confidence_risk
                  ]}
                />
              </div>
            </TabsContent>

            {/* Confidence Tab */}
            <TabsContent value="confidence" className="space-y-6 mt-6">
              <div className="flex items-center gap-6 mb-6">
                <ScoreGauge score={intelligence.confidence_score.overall_score} label={confidenceConfig.label} size="lg" />
                <div>
                  <h2 className="text-xl font-bold">Confidence Score Breakdown</h2>
                  <p className="text-sm text-muted-foreground">
                    How much to trust the analysis. Target above 60.
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ScoreBreakdownCard
                  title="Qualité des Données"
                  icon={Shield}
                  components={[
                    intelligence.confidence_score.comp_set_strength,
                    intelligence.confidence_score.data_completeness
                  ]}
                />
                <ScoreBreakdownCard
                  title="Fiabilité des Prévisions"
                  icon={Target}
                  components={[
                    intelligence.confidence_score.forecast_reliability,
                    intelligence.confidence_score.partner_reliability,
                    intelligence.confidence_score.scenario_stability
                  ]}
                />
              </div>
            </TabsContent>

            {/* AI Memo Tab */}
            <TabsContent value="memo" className="space-y-6 mt-6">
              <Card data-testid="ai-memo">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Brain size={20} />
                    Investment Memo
                  </CardTitle>
                  <CardDescription>AI-generated summary based on structured analysis</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Deal Memo */}
                  <div>
                    <h3 className="font-semibold mb-2">Deal Summary</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {intelligence.ai_commentary.deal_memo_summary}
                    </p>
                  </div>

                  {/* Risk Commentary */}
                  <div>
                    <h3 className="font-semibold mb-2">Risk Assessment</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {intelligence.ai_commentary.risk_commentary}
                    </p>
                  </div>

                  {/* Exit Commentary */}
                  <div>
                    <h3 className="font-semibold mb-2">Exit Analysis</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {intelligence.ai_commentary.exit_commentary}
                    </p>
                  </div>

                  {/* Partner Commentary */}
                  <div>
                    <h3 className="font-semibold mb-2">Partner Assessment</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {intelligence.ai_commentary.partner_commentary}
                    </p>
                  </div>

                  {/* Data Quality Warning */}
                  {intelligence.ai_commentary.data_quality_warning && (
                    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-start gap-3">
                        <Warning size={20} className="text-amber-600 mt-0.5 shrink-0" />
                        <div>
                          <p className="font-medium text-amber-800">Data Quality Warning</p>
                          <p className="text-sm text-amber-700 mt-1">
                            {intelligence.ai_commentary.data_quality_warning}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Link to Deal Detail */}
          <div className="flex justify-end">
            <Link to={`/deal/${selectedDealId}`}>
              <Button variant="outline" className="gap-2">
                View Deal Details
                <CaretRight size={16} />
              </Button>
            </Link>
          </div>
        </>
      ) : (
        <Card>
          <CardContent className="py-12">
            <EmptyState
              icon={Brain}
              title="Sélectionnez un Deal"
              description="Choisissez un deal dans le menu pour voir l'analyse IA"
            />
          </CardContent>
        </Card>
      )}

      {/* Page Help */}
      <PageHelpBlock 
        pageKey="ai-intelligence" 
        content={pageExplanations.aiIntelligence} 
      />
    </div>
  );
};

export default AIIntelligence;
